import React, { FC, memo, useEffect, useState } from 'react';
import { joinMenuSections } from '@components/ActionsMenu/helpers';
import { IActionItem } from '@components/ActionsMenu/types';
import useCiaData from '@components/EntityDrawers/components/CiaInputsGroup/hooks/useCiaData';
import { ContextMenu } from '@components/MainStage/ContextMenu/ContextMenu';
import { DeleteNodeModal } from '@components/MainStage/ContextMenu/NodeContextMenu/DeleteNodeModal';
import RelationshipsMenu from '@components/MainStage/ContextMenu/RelationshipsMenu';
import useOpenRelation from '@components/MainStage/ContextMenu/RelationshipsMenu/hooks/useOpenRelation';
import useRelationSections from '@components/MainStage/ContextMenu/RelationshipsMenu/hooks/useRelationSections';
import { isFocusEligible } from '@components/MainStage/focusOn.helpers';
import {
  useIsFocusActive,
  useIsNodeFocused,
} from '@components/MainStage/focusOn.store';
import useDeleteKey from '@components/MainStage/hooks/useDeleteKey';
import useFocusOn from '@components/MainStage/hooks/useFocusOn';
import { getHoverState } from '@components/MainStage/hover.store';
import {
  getMultiSelectionState,
  useMultiSelectionCount,
} from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { ContextMenuProps } from '@components/MainStage/types';
import { NODE_MENU_ITEM_HEIGHT } from '@constants/canvas/general';
import { useProject } from '@context/Project/ProjectProvider';
import useAssets from '@hooks/useAssets';
import useCanEditModel from '@hooks/useCanEditModel';
import useToggle from '@hooks/useToggle';
import {
  NodeActionType,
  NodeDTO,
  NodeDTOTypes,
} from '@store/services/nodes/types';
import { themePalette } from '@theme/muiTheme';
import {
  CloseCircleIcon,
  FocusOnIcon,
  RelationshipsIcon,
  ReportIcon,
  TrashIcon,
} from '@utils/iconsDefs';
import {
  getMostCapableNode,
  isCiaActionVisible,
  isTargetScopeVisible,
  NODE_ACTIONS,
  REMOVE_FROM_ASSET_ACTION,
} from '@utils/nodeActions';
import { JustificationReportModal } from '@views/ReportsModel/components/JustificationReport/JustificationReportModal';

import { AddCiaModal, CiaMenuActionType } from './AddCiaModal';
import { AddScopeModal } from './AddScopeModal';
import {
  getManageRelationshipsMode,
  hasDirectRelationship,
} from './manageRelationshipsMenu.helpers';
import { NodeActionSubmenu } from './NodeActionSubmenu';
import { useCreateRelationships } from './useCreateRelationships';
import { useDeleteSelection } from './useDeleteSelection';
import { useRemoveNodesFromAsset } from './useRemoveNodesFromAsset';

const NodeContextMenu: FC<ContextMenuProps> = ({ isOpen, onClose, menu }) => {
  const { getNodeById, nodes, edges } = useStore();
  const { handleOpenRelation } = useOpenRelation();
  const [isDeleteOpen, deleteControls] = useToggle<null | string>();
  const [isJustificationReportOpen, justificationReportControls] = useToggle<
    null | boolean
  >();
  const [deleteId, setDeleteId] = useState<null | string>(null);
  const [isScopeOpen, scopeControls] = useToggle<boolean>();
  const [scopeTargetIds, setScopeTargetIds] = useState<string[]>([]);
  const [ciaActionType, setCiaActionType] = useState<CiaMenuActionType | null>(
    null,
  );
  const [ciaTargetIds, setCiaTargetIds] = useState<string[]>([]);
  const { isCiaActivated, questionnaire } = useCiaData();
  const canEdit = useCanEditModel();

  const nodeId = deleteId || menu?.id || '';
  const node = getNodeById(nodeId);
  const relationSections = useRelationSections({
    nodeId: menu?.type === 'node' ? nodeId : '',
  });

  const multiSelectionCount = useMultiSelectionCount();
  const { enterFocus, exitFocus } = useFocusOn();
  const isFocusActive = useIsFocusActive();
  const isNodeInFocus = useIsNodeFocused(nodeId);

  const canShowFocusOn =
    isOpen &&
    !isFocusActive &&
    !!node &&
    isFocusEligible(node, multiSelectionCount, nodes);
  const canShowExitFocus = isFocusActive && isNodeInFocus;

  const isNodeWithJustificationReport =
    node?.data?.dto?.type === NodeDTOTypes.BusinessAttribute ||
    node?.data?.dto?.type === NodeDTOTypes.SecurityAttribute;

  const {
    toolbox: { mode },
  } = useProject();

  const { selectedAssetId } = useAssets();
  const { selectedIds } = getMultiSelectionState();
  const targetIds =
    selectedIds.length > 1 && selectedIds.includes(nodeId)
      ? selectedIds
      : [nodeId];
  const isMultiSelectActive = targetIds.length > 1;
  const targetNodes = targetIds
    .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
    .filter((dto): dto is NodeDTO => Boolean(dto));

  useEffect(() => {
    const menuNodeId = menu?.type === 'node' ? menu.id : null;
    if (!isOpen || !menuNodeId || isMultiSelectActive) return undefined;

    getHoverState().setPinned(menuNodeId);

    return () => getHoverState().unpin(menuNodeId);
  }, [isOpen, menu?.type, menu?.id, isMultiSelectActive]);

  const representativeNode = mode
    ? getMostCapableNode(targetNodes, mode)
    : undefined;
  const showTargetScope =
    canEdit && mode
      ? isTargetScopeVisible(targetNodes, mode, selectedAssetId)
      : false;
  const targetScopeConfig = NODE_ACTIONS[NodeActionType.AddTargetScope];

  const showCiaActions =
    canEdit &&
    mode &&
    isCiaActivated &&
    !!questionnaire &&
    isCiaActionVisible(targetNodes, mode);

  const handleOpenCiaModal = (actionType: CiaMenuActionType) => () => {
    setCiaTargetIds(targetIds);
    setCiaActionType(actionType);
  };

  const ciaMenuItems: IActionItem[] = showCiaActions
    ? (
        [
          NodeActionType.AddAssurance,
          NodeActionType.AddApplicability,
          NodeActionType.AddCoverage,
        ] as CiaMenuActionType[]
      ).flatMap((actionType) => {
        const config = NODE_ACTIONS[actionType];
        const criterias = config?.ciaDimension
          ? questionnaire.cia_criteria?.[config.ciaDimension]?.criterias ?? []
          : [];

        return config && criterias.length > 0
          ? [
              {
                icon: config.icon,
                text: config.label,
                onClick: handleOpenCiaModal(actionType),
              },
            ]
          : [];
      })
    : [];

  const actionMenuItems: IActionItem[] =
    canEdit && mode && representativeNode
      ? Object.values(NODE_ACTIONS)
          .filter(
            (config) =>
              config.statusOptions &&
              config.getEligibility(representativeNode, mode).eligible,
          )
          .map((config) => ({
            icon: config.icon,
            text: config.label,
            subComponent: (
              <NodeActionSubmenu
                config={config}
                nodeIds={targetIds}
                onCloseMenu={onClose}
              />
            ),
          }))
      : [];

  const manageRelationshipsMode = getManageRelationshipsMode(
    targetIds,
    (a, b) => hasDirectRelationship(edges, a, b),
    relationSections.length > 0,
  );

  const createRelationships = useCreateRelationships(targetIds, onClose);
  const removeFromAsset = useRemoveNodesFromAsset(targetIds, onClose);
  const deleteSelection = useDeleteSelection(targetIds, onClose);
  const createRelationshipsConfig =
    NODE_ACTIONS[NodeActionType.CreateRelationships];

  const handleOpenDeleteModal = () => {
    deleteControls.on();
    onClose();
    setDeleteId(null);
  };

  const handleOpenJustificationReportModal = () => {
    justificationReportControls.on();
    onClose();
  };

  const handleOpenScopeModal = () => {
    setScopeTargetIds(targetIds);
    scopeControls.on();
  };

  let relationsMenuItem: IActionItem | null = null;

  if (manageRelationshipsMode.kind === 'submenu') {
    relationsMenuItem = {
      icon: <RelationshipsIcon fill="currentColor" />,
      text: 'Manage relationships',
      subComponent: <RelationshipsMenu nodeId={nodeId} onCloseMenu={onClose} />,
      popoverProps: {
        sx: {
          '& .MuiPopover-paper': { borderRadius: 2 },
          '.MuiPaper-root': {
            width: 320,
            maxHeight: 500,
            overflow: 'hidden',
            display: 'flex',
          },
        },
      },
    };
  } else if (manageRelationshipsMode.kind === 'direct') {
    const [firstId, secondId] = manageRelationshipsMode.pair;

    relationsMenuItem = {
      icon: <RelationshipsIcon fill="currentColor" />,
      text: 'Manage relationships',
      onClick: () => {
        handleOpenRelation(firstId, secondId);
        onClose();
      },
    };
  }

  const focusMenuItems: IActionItem[] = [
    ...(canShowFocusOn
      ? [
          {
            icon: <FocusOnIcon fill="currentColor" />,
            text: 'Focus on',
            onClick: () => {
              enterFocus(nodeId, node!.data.dto.name);
              onClose();
            },
          },
        ]
      : []),

    ...(canShowExitFocus
      ? [
          {
            icon: <FocusOnIcon fill="currentColor" />,
            text: 'Exit focus on',
            onClick: () => {
              exitFocus();
              onClose();
            },
          },
        ]
      : []),
  ];

  const contextMenuItems: IActionItem[] = joinMenuSections([
    [
      ...focusMenuItems,

      ...(relationsMenuItem ? [relationsMenuItem] : []),

      ...(canEdit && createRelationships.isVisible && createRelationshipsConfig
        ? [
            {
              icon: createRelationshipsConfig.icon,
              text: createRelationshipsConfig.label,
              disabled: createRelationships.isLoading,
              onClick: createRelationships.start,
            },
          ]
        : []),
    ],

    [
      ...actionMenuItems,

      ...(showTargetScope && targetScopeConfig
        ? [
            {
              icon: targetScopeConfig.icon,
              text: targetScopeConfig.label,
              onClick: handleOpenScopeModal,
            },
          ]
        : []),
    ],

    ciaMenuItems,

    isNodeWithJustificationReport
      ? [
          {
            icon: <ReportIcon fill="currentColor" />,
            text: 'Reports',
            subItems: [
              {
                text: 'Justification statement',
                onClick: handleOpenJustificationReportModal,
              },
            ],
          },
        ]
      : [],

    canEdit && removeFromAsset.isVisible
      ? [
          {
            icon: (
              <CloseCircleIcon fill="currentColor" width={20} height={20} />
            ),
            text: REMOVE_FROM_ASSET_ACTION.label,
            disableHoverColor: true,
            color: themePalette.system.error,
            disabled: removeFromAsset.isLoading,
            onClick: removeFromAsset.start,
          },
        ]
      : [],

    canEdit && deleteSelection.isVisible
      ? [
          {
            icon: <TrashIcon />,
            text: 'Delete',
            disableHoverColor: true,
            color: themePalette.system.error,
            disabled: isMultiSelectActive && deleteSelection.isLoading,
            onClick: isMultiSelectActive
              ? deleteSelection.start
              : handleOpenDeleteModal,
          },
        ]
      : [],
  ]);

  useDeleteKey('node', (id) => {
    if (!canEdit) return;

    setDeleteId(id);
    deleteControls.on();
  });

  if (contextMenuItems.length === 0) return null;

  const dividersHeight = contextMenuItems.filter((item) => item.divider).length;

  return (
    <>
      <DeleteNodeModal
        open={isDeleteOpen}
        onClose={deleteControls.off}
        nodeId={nodeId}
      />

      {mode && (
        <AddScopeModal
          open={isScopeOpen}
          onClose={scopeControls.off}
          nodeIds={scopeTargetIds}
          mode={mode}
        />
      )}

      {mode && ciaActionType && (
        <AddCiaModal
          open
          onClose={() => setCiaActionType(null)}
          actionType={ciaActionType}
          nodeIds={ciaTargetIds}
          mode={mode}
        />
      )}

      {createRelationships.modal}

      {removeFromAsset.modal}

      {deleteSelection.modal}

      {isNodeWithJustificationReport && (
        <JustificationReportModal
          open={isJustificationReportOpen}
          onClose={justificationReportControls.off}
          node={node}
        />
      )}

      {isOpen &&
        !isScopeOpen &&
        !ciaActionType &&
        menu &&
        menu.type === 'node' && (
          <ContextMenu
            menuItems={contextMenuItems}
            {...menu}
            top={
              menu.top -
              contextMenuItems.length * NODE_MENU_ITEM_HEIGHT -
              dividersHeight
            }
          />
        )}
    </>
  );
};

export default memo(NodeContextMenu);
